import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiCalendar, FiBarChart3, FiDownload, FiUpload, FiCheckCircle, FiClock, FiFileText, FiShield, FiStar, FiArrowRight, FiTrendingUp, FiInfo, FiBookOpen, FiTarget, FiAward, FiZap } = FiIcons;

const AboutPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: FiUsers,
      title: "Bulk Student Management",
      description: "Add students individually or import entire lists with one click. No manual entry required for large classes."
    },
    {
      icon: FiCalendar,
      title: "Daily Attendance Tracking",
      description: "Quickly mark attendance for any date with our intuitive checkbox interface. Track present, absent, and unmarked students."
    },
    {
      icon: FiBarChart3,
      title: "Comprehensive Analytics",
      description: "View detailed attendance statistics, student performance trends, and daily attendance rates."
    },
    {
      icon: FiDownload,
      title: "Report Generation",
      description: "Export professional attendance reports as Word documents with timestamps for record-keeping."
    },
    {
      icon: FiUpload,
      title: "Data Import/Export",
      description: "Save and load your attendance data as JSON files. Perfect for backup and sharing between devices."
    },
    {
      icon: FiShield,
      title: "Privacy & Security",
      description: "All data stays in your browser. No external databases or cloud storage required."
    }
  ];

  const benefits = [
    {
      icon: FiClock,
      title: "Save Time",
      description: "Reduce attendance management time by 90% with our streamlined interface."
    },
    {
      icon: FiTrendingUp,
      title: "Improve Accuracy",
      description: "Eliminate manual errors and ensure accurate attendance records."
    },
    {
      icon: FiCheckCircle,
      title: "Free Forever",
      description: "No hidden costs, subscriptions, or premium features. Completely free."
    }
  ];

  const instructions = [
    {
      step: 1,
      title: "Add Your Students",
      description: "Start by adding students individually or paste multiple names at once (one per line) for bulk import."
    },
    {
      step: 2,
      title: "Select a Date",
      description: "Choose any date to take attendance. The calendar makes it easy to navigate to any day."
    },
    {
      step: 3,
      title: "Mark Attendance",
      description: "Check off students who are present. Unchecked boxes remain unmarked, giving you flexibility."
    },
    {
      step: 4,
      title: "Analyze & Export",
      description: "View analytics to track patterns and export reports for administrative purposes."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Article Header */}
      <article className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <SafeIcon icon={FiInfo} className="w-8 h-8 mr-3" />
              <h1 className="text-4xl md:text-5xl font-bold">About Free Attendance Tracker</h1>
            </div>
            <div className="flex items-center justify-center text-blue-100 mb-6">
              <SafeIcon icon={FiBookOpen} className="w-5 h-5 mr-2" />
              <span className="text-lg">A comprehensive guide to modern attendance management</span>
            </div>
            <div className="flex items-center justify-center space-x-4 text-blue-200 text-sm">
              
              <span>•</span>
              <span>Reading time: 5 min</span>
              <span>•</span>
              <span>Category: Educational Tools</span>
            </div>
          </div>
        </div>
      </article>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Revolutionizing Classroom Attendance Management</h2>
          
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
            <p className="mb-6">
              In today's educational landscape, managing student attendance efficiently is more crucial than ever. 
              Teachers and administrators spend countless hours on administrative tasks that could be better spent on 
              actual teaching and student engagement. This is where our <strong>Free Attendance Tracker App</strong> 
              comes into play - a powerful, intuitive solution designed to streamline attendance management 
              without the burden of costly subscriptions or complex software installations.
            </p>
            
            <p className="mb-6">
              As educators ourselves, we understand the challenges of maintaining accurate attendance records while 
              juggling multiple responsibilities. Traditional methods like paper-based systems or expensive software 
              solutions often fall short, either being too time-consuming or financially prohibitive. Our attendance 
              tracker addresses these pain points head-on, offering a comprehensive solution that's both powerful and 
              accessible to everyone.
            </p>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
            <SafeIcon icon={FiTarget} className="w-8 h-8 mr-3 text-indigo-600" />
            Key Features That Make a Difference
          </h2>
          
          <p className="text-lg text-gray-700 mb-8">
            Our free attendance tracker app is packed with features designed specifically for educational environments:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <SafeIcon icon={feature.icon} className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="mb-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
            <SafeIcon icon={FiAward} className="w-8 h-8 mr-3 text-indigo-600" />
            Why Educators Choose Our Free Attendance Tracker
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={benefit.icon} className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">{benefit.title}</h3>
                <p className="text-gray-600 text-center">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
            <SafeIcon icon={FiZap} className="w-8 h-8 mr-3 text-indigo-600" />
            Getting Started: A Step-by-Step Guide
          </h2>
          
          <p className="text-lg text-gray-700 mb-8">
            Our attendance tracker is designed with simplicity in mind. Here's how you can get started in minutes:
          </p>
          
          <div className="space-y-6">
            {instructions.map((instruction) => (
              <div key={instruction.step} className="flex items-start bg-white rounded-xl border border-gray-200 p-6">
                <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0">
                  {instruction.step}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{instruction.title}</h3>
                  <p className="text-gray-600">{instruction.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Technical Excellence */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Built with Modern Technology</h2>
          
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-6">
              Our free attendance tracker leverages cutting-edge web technologies to deliver a seamless experience 
              without compromising on privacy or performance. Built with React and modern JavaScript, the application 
              runs entirely in your browser, ensuring your data remains private and secure on your device.
            </p>
            
            <p className="mb-6">
              The responsive design ensures optimal performance across all devices - from desktop computers to 
              tablets and smartphones. This flexibility allows teachers to take attendance using whatever device 
              is most convenient, whether in the classroom, office, or on the go.
            </p>
          </div>
        </section>

        {/* SEO Content */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Future of Attendance Management is Here</h2>
          
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-6">
              As educational institutions continue to digitize their operations, having reliable, free tools like 
              our attendance tracker becomes increasingly important. We believe that effective attendance management 
              shouldn't be a luxury reserved for well-funded schools - it should be accessible to everyone.
            </p>
            
            <p className="mb-6">
              Our commitment to providing this <strong>free attendance tracker app</strong> stems from our belief 
              in democratizing educational technology. By removing cost barriers and technical complexities, we're 
              empowering educators to focus on what truly matters: student success and educational excellence.
            </p>
            
            <p className="mb-6">
              Whether you're managing a small classroom of 20 students or a large lecture hall with hundreds, 
              our attendance tracker scales to meet your needs. The intuitive interface requires minimal training, 
              making it perfect for teachers of all technical skill levels.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center py-12 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Attendance Management?</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Join thousands of educators who have already simplified their attendance tracking with our free tool.
          </p>
          <div className="flex justify-center">
            <Link
              to="/tracker"
              className="px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors inline-flex items-center justify-center"
            >
              <SafeIcon icon={FiStar} className="mr-2 w-5 h-5" />
              Start Using the Tracker
            </Link>
          </div>
        </section>

        {/* Additional SEO Content */}
        <section className="mt-16 pt-8 border-t border-gray-200">
          <div className="prose prose-lg max-w-none text-gray-700">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Frequently Asked Questions</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Is this attendance tracker really free?</h4>
                <p className="text-gray-600">
                  Yes, absolutely! Our free attendance tracker app is completely free with no hidden costs, 
                  subscriptions, or premium features. All functionality is available to every user at no charge.
                </p>
              </div>
              
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">How is my data stored?</h4>
                <p className="text-gray-600">
                  Your attendance data is stored locally in your browser using localStorage. No data is sent to 
                  external servers or stored in cloud databases, ensuring complete privacy and security.
                </p>
              </div>
              
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Can I use this on mobile devices?</h4>
                <p className="text-gray-600">
                  Yes, our attendance tracker is fully responsive and works seamlessly on smartphones, tablets, 
                  and desktop computers. The interface automatically adapts to your screen size.
                </p>
              </div>
              
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">How many students can I track?</h4>
                <p className="text-gray-600">
                  There's no limit to the number of students you can track with our free attendance tracker. 
                  Whether you have 5 students or 500, the app will handle your needs efficiently.
                </p>
              </div>
            </div>
          </div>
        </section>
      </article>
    </div>
  );
};

export default AboutPage;